import { type Pitch, type Step, type Alter, STEPS, midiOf, diatonicIndex, fromDiatonicIndex, spellingsOf, spellOnStep } from './pitch';
import { keyAlterFor } from './keysig';

/**
 * Spell a MIDI number in a key (§4.5): prefer the key's diatonic spelling,
 * otherwise sharps in keys with fifths ≥ 0 and flats in flat keys.
 */
export function spellMidi(midi: number, fifths = 0, preferFlats?: boolean): Pitch {
  // 1. Diatonic spelling in the key.
  for (const step of STEPS) {
    const p = spellOnStep(midi, step);
    if (p && p.alter === keyAlterFor(fifths, step)) return p;
  }
  const flats = preferFlats ?? fifths < 0;
  const cands = spellingsOf(midi, 1);
  const pick = cands.find((p) => (flats ? p.alter <= 0 : p.alter >= 0)) ?? cands[0];
  return pick;
}

/**
 * Enharmonic alternatives used by the toggle-enharmonic command (Mod+E):
 * single-accidental spellings first; double accidentals only when there is
 * no single alternative (D, G, A).
 */
export function enharmonicCycle(p: Pitch): Pitch[] {
  const midi = midiOf(p);
  const singles = spellingsOf(midi, 1);
  const list = singles.length >= 2 ? singles : spellingsOf(midi, 2);
  return list;
}

/** Next spelling in the enharmonic cycle. */
export function toggleEnharmonic(p: Pitch): Pitch {
  const list = enharmonicCycle(p);
  const i = list.findIndex((q) => q.step === p.step && q.alter === p.alter && q.octave === p.octave);
  if (i < 0) return list[0] ?? p;
  return list[(i + 1) % list.length];
}

/**
 * Letter entry (§4.1): the pitch with this letter nearest to the previous
 * note. Alteration comes from the key signature. With no previous note, the
 * octave closest to the staff's middle line is chosen.
 */
export function nearestPitchForLetter(step: Step, prev: Pitch | null, fifths: number, middleLine: Pitch): Pitch {
  const alter = keyAlterFor(fifths, step);
  if (!prev) {
    const mid = diatonicIndex(middleLine);
    const base = Math.floor(mid / 7);
    let best: Pitch | null = null;
    let bestDist = Infinity;
    for (const o of [base - 1, base, base + 1]) {
      const c: Pitch = { step, alter, octave: o };
      const dist = Math.abs(diatonicIndex(c) - mid);
      // On a tie prefer the upper candidate.
      if (dist < bestDist || (dist === bestDist && best && diatonicIndex(c) > diatonicIndex(best))) {
        best = c;
        bestDist = dist;
      }
    }
    return best!;
  }
  const target = midiOf(prev);
  let best: Pitch | null = null;
  let bestDist = Infinity;
  for (const o of [prev.octave - 1, prev.octave, prev.octave + 1]) {
    const c: Pitch = { step, alter, octave: o };
    const dist = Math.abs(midiOf(c) - target);
    if (dist < bestDist || (dist === bestDist && best && midiOf(c) > midiOf(best))) {
      best = c;
      bestDist = dist;
    }
  }
  return best!;
}

/**
 * Interval append (§4.2): a diatonic interval of number n (2…8) above (dir=1)
 * or below (dir=-1) a base pitch, with the alteration taken from the key.
 */
export function diatonicInterval(base: Pitch, n: number, dir: 1 | -1, fifths: number): Pitch {
  const d = diatonicIndex(base) + dir * (n - 1);
  const p = fromDiatonicIndex(d);
  return { ...p, alter: keyAlterFor(fifths, p.step) };
}

/** Move a pitch by whole octaves. */
export function octaveShift(p: Pitch, octaves: number): Pitch {
  return { ...p, octave: p.octave + octaves };
}

/** Move a pitch one diatonic step up/down, keeping the key's alteration. */
export function diatonicStep(p: Pitch, delta: number, fifths: number): Pitch {
  const q = fromDiatonicIndex(diatonicIndex(p) + delta);
  return { ...q, alter: keyAlterFor(fifths, q.step) };
}

/** Pitch at a diatonic staff position relative to the middle line (0 = middle line, +1 = space above). */
export function pitchAtStaffPosition(pos: number, middleLine: Pitch, fifths: number): Pitch {
  const q = fromDiatonicIndex(diatonicIndex(middleLine) + pos);
  return { ...q, alter: keyAlterFor(fifths, q.step) };
}

export function staffPosition(p: Pick<Pitch, 'step' | 'octave'>, middleLine: Pitch): number {
  return diatonicIndex(p) - diatonicIndex(middleLine);
}

export interface AccidentalInput {
  pitch: Pitch;
  /** Tied continuation of a note from the previous bar: never shows an accidental. */
  tiedFromPrevBar?: boolean;
  /** Tied continuation within the bar: inherits, shows nothing. */
  tiedFromPrev?: boolean;
  display?: 'auto' | 'cautionary' | 'hidden';
}

/** Which accidental (if any) to draw. `null` = none. 0 = natural sign. */
export type AccidentalMark = { alter: Alter; cautionary: boolean } | null;

/**
 * Accidental display for one bar of one staff (§4.7). `notes` must be in time
 * order (chord notes in any order). State is keyed by staff position.
 */
export function computeAccidentals(notes: AccidentalInput[], fifths: number): AccidentalMark[] {
  const state = new Map<number, Alter>();
  const keyAlter = (p: Pitch): Alter => {
    const pos = diatonicIndex(p);
    return state.has(pos) ? state.get(pos)! : keyAlterFor(fifths, p.step);
  };
  return notes.map((n) => {
    const pos = diatonicIndex(n.pitch);
    if (n.display === 'hidden') {
      state.set(pos, n.pitch.alter);
      return null;
    }
    if (n.tiedFromPrevBar || n.tiedFromPrev) {
      state.set(pos, n.pitch.alter);
      return null;
    }
    const current = keyAlter(n.pitch);
    let mark: AccidentalMark = null;
    if (n.pitch.alter !== current) mark = { alter: n.pitch.alter, cautionary: false };
    else if (n.display === 'cautionary') mark = { alter: n.pitch.alter, cautionary: true };
    state.set(pos, n.pitch.alter);
    return mark;
  });
}

